import { ENV, WORKTREE_PORT } from './environments.js';
import { DOMAIN } from './resources.js';
export const ASSETS_DIR = 'assets';
export const API_PATH = '/api';
/**
 * Name of the secret header CloudFront injects on every origin request to the API gateways.
 *
 * The HTTP API gateways are publicly reachable, so callers could hit them directly and bypass the
 * WAF (which only fronts CloudFront). CloudFront injects this header as an origin custom header
 * (which overrides any viewer-supplied value, so it cannot be spoofed); the app lambdas reject any
 * request that does not carry the matching per-app secret. Lowercase to match HTTP API v2, which
 * lowercases header keys. See SIG-125.
 */
export const CLOUDFRONT_ORIGIN_SECRET_HEADER = 'x-origin-secret';
export const PATIENT_AUTH_CALLBACK_PATH = `/app/login/callback`;
/**
 * Define an app.
 * @param app - The app to define.
 * @returns The app.
 * @__NO_SIDE_EFFECTS__
 */
export function defineApp(app) {
    return app;
}
export const PATIENT_APP = defineApp({
    name: 'patient',
    domain: DOMAIN,
    devPort: 0,
});
export const PATIENT_APP_SENTRY = {
    project: 'patient-app',
    dsn: 'https://fa179714d609d1074cbfa555d5379db7@o4509712435773440.ingest.de.sentry.io/4509712542138448',
};
export const PATIENT_API = defineApp({
    name: 'patient-api',
    devPort: 1,
});
export const PATIENT_API_SENTRY = {
    dsn: 'https://2eb5820b5984744040519bea14a83f1c@o4509712435773440.ingest.de.sentry.io/4510501501206608',
    project: 'patient-api',
};
export const DOCTOR_APP = defineApp({
    name: 'doctor',
    domain: `doctor.${DOMAIN}`,
    devPort: 2,
});
export const DOCTOR_APP_SENTRY = {
    dsn: 'https://3f46563f93acc40702716018424e2524@o4509712435773440.ingest.de.sentry.io/4509712508780624',
    project: 'doctor-app',
};
export const DOCTOR_API = defineApp({
    name: 'doctor-api',
    devPort: 3,
});
export const DOCTOR_API_SENTRY = {
    dsn: 'https://cacfb5266326819426cf685c1015ea6c@o4509712435773440.ingest.de.sentry.io/4510501506252880',
    project: 'doctor-api',
};
export const DOCTOR_COPILOT_APP = defineApp({
    name: 'copilot',
    domain: `copilot.${DOMAIN}`,
    devPort: 8,
});
export const DOCTOR_COPILOT_APP_SENTRY = {
    dsn: 'https://44185821996bd84c6f2a0ba1c8fcd981@o4509712435773440.ingest.de.sentry.io/4510501510185041',
    project: 'copilot-app',
};
export const DOCTOR_COPILOT_API = defineApp({
    name: 'copilot-api',
    devPort: 9,
});
export const DOCTOR_COPILOT_API_SENTRY = {
    dsn: 'https://5403f8d29df7277a954fc11b9b46624b@o4509712435773440.ingest.de.sentry.io/4510501512478800',
    project: 'copilot-api',
};
export const API_APP = defineApp({
    name: 'api',
    domain: `api.${DOMAIN}`,
    devPort: 10,
});
export const STORYBOOK_APP = defineApp({
    name: 'storybook',
    domain: `storybook.${DOMAIN}`,
    devPort: 11,
});
export const FILE_GATEWAY_APP = defineApp({
    name: 'file-gateway',
    domain: `files.${DOMAIN}`,
    // Offset 12 (port 3012 with default worktree base) collides with Docker on macOS.
    devPort: 13,
});
/**
 * Every app that runs a local dev server binding a worktree port — including the API dev servers.
 * Consumers that only care about browseable apps with a public domain filter on `app.domain`
 * (e.g. the Caddy reverse proxy and the dev tunnel skip domain-less APIs).
 */
export const APPS = [
    PATIENT_APP,
    PATIENT_API,
    DOCTOR_APP,
    DOCTOR_API,
    DOCTOR_COPILOT_APP,
    DOCTOR_COPILOT_API,
    API_APP,
    STORYBOOK_APP,
    FILE_GATEWAY_APP,
];
/**
 * Get the public URL for an app.
 * In local mode, uses the Caddy proxy hostname (http).
 * In production/staging, uses https with the app domain.
 */
export function getAppUrl(app) {
    switch (ENV) {
        case 'local':
            return `http://localhost:${getLocalPort(app.devPort)}`;
        default:
            return `https://${app.domain}`;
    }
}
/**
 * Get the local port for a given port offset.
 * @param portShift - The port offset relative to the worktree base port.
 * @param worktreePort - Optional worktree base port override. Defaults to WORKTREE_PORT env var.
 */
export function getLocalPort(portShift, worktreePort) {
    const basePort = worktreePort ?? WORKTREE_PORT;
    return basePort + portShift;
}
/**
 * Get the port an app is served on over the dev tunnel.
 *
 * Always the canonical dev port — deliberately not `getLocalPort`, which shifts per worktree in
 * `ENV=local`. The tailnet address must not move when a developer switches worktree, so a URL
 * opened on a phone keeps working.
 *
 * @param app - The app to get the tunnel port for.
 * @__NO_SIDE_EFFECTS__
 */
export function getTunnelPort(app) {
    return getLocalPort(app.devPort, 3000);
}
//# sourceMappingURL=apps.js.map